import React, {useEffect, useMemo, useState} from 'react'
import 'reactjs-popup/dist/index.css';

//import redux components
import {useDispatch, useSelector} from 'react-redux'
import {changeActiveCourse} from '../redux/activeCourseName/activeCourseNameSlice'

//  local components
import CourseMetaInfo from './courseMetaInfo'
import CourseHeroFooter from './courseHeroFooter'

// API
import {useGetPathwayAvailabilityQuery} from "../api/courses";

//SVG COMPONENTS - loaded with @svgr/webpack
import IconVideoPurple from '../../../../../img/icon-video-purple.svg';
import IconPodiumPurple from '../../../../../img/icon-podium-purple.svg';
import CaretGoldRight from '../../../../../img/caret-gold-right.svg';
import {isEmpty} from "underscore";

/**
 * List of the courses that are online only.
 *
 * @type {string[]}
 */
const ONLINE_ONLY_COURSE_NUMBERS = ["165", "246"];

// Canonical pathway map used for UI rendering, membership checks, and status messaging.
const PATHWAYS = {
    '1': ['165', '246', '405', '505', '603'],
    '2': ['105', '205', '305', '405', '505', '603'],
};

/**
 * Normalize course numbers to stable string keys for comparison and object lookup.
 * Example: 105 -> "105", " 246 " -> "246".
 */
function normalizeCourseNumber(courseNumber) {
    if (courseNumber === null || courseNumber === undefined) {
        return '';
    }

    return String(courseNumber).trim();
}

/**
 * Return which pathway(s) a course number belongs to.
 * Shared courses return both pathway ids.
 */
function getPathwayMembership(courseNumber) {
    const membership = [];

    if (PATHWAYS['1'].includes(courseNumber)) {
        membership.push('1');
    }

    if (PATHWAYS['2'].includes(courseNumber)) {
        membership.push('2');
    }

    return membership;
}

/**
 * Resolve display/course metadata type label for the pathway legend and aria text.
 *
 * If the course's `methodRecordId` is part of `ONLINE_ONLY_COURSE_NUMBERS`, then it will always return 'online'.
 */
function getCourseTypeLabel(course) {
    if (ONLINE_ONLY_COURSE_NUMBERS.includes(course.courseNumber)) {
        return 'online';
    }

    return course && course.onlineOnly ? 'online' : 'in-person';
}

/**
 * Course Pathway Hero component
 * @param {Object} courseDetailsProp - All the course details
 * @param {Object} courseNames - The names of the courses, used for indexing the swiper and page info
 */
function CoursePathwayHero({courseDetailsProp, courseNames}) {
    // get that redux dispatcher!
    const dispatch = useDispatch();
    // get our active course name from the redux store
    const activeCourseName = useSelector((state) => state.activeCourseName.value)
    // Screen-reader live-region content describing the current selection/pathway.
    const [statusMessage, setStatusMessage] = useState('');
    // Tracks tooltip dismissed via Escape for the currently focused/hovered course.
    const [dismissedTooltip, setDismissedTooltip] = useState(null);

    // the active course details
    const courseDetails = courseDetailsProp[activeCourseName] || {};

    // Build a list of all methodRecordIds for pathway courses so we can fire one availability request for all of them at once.
    const pathwayTemplateIds = useMemo(() => {
        return courseNames
            .map((key) => courseDetailsProp[key]?.methodRecordId)
            .filter(Boolean);
    }, [courseDetailsProp, courseNames]);

    const {
        data: pathwayAvailability = {},
        isLoading: availabilityLoading,
    } = useGetPathwayAvailabilityQuery(pathwayTemplateIds, {
        skip: pathwayTemplateIds.length === 0,
    });

    // Build a fast lookup: course number -> redux course key.
    // This decouples rendered pathway numbers from internal course object keys.
    const numberToCourseKey = useMemo(() => {
        const lookup = {};

        courseNames.forEach((courseKey) => {
            const details = courseDetailsProp[courseKey];
            if (!details) {
                return;
            }

            const number = normalizeCourseNumber(details.courseNumber);
            if (number) {
                lookup[number] = courseKey;
            }
        });

        return lookup;
    }, [courseDetailsProp, courseNames]);

    const activeCourseNumber = normalizeCourseNumber(courseDetails.courseNumber);

    function getPathwayLabel(membership) {
        if (membership.length > 1) {
            return 'shared by pathways 1 and 2';
        }

        return `in pathway ${membership[0] || '2'}`;
    }

    function handleCourseClick(courseNumber) {
        // Translate UI course number to Redux key before dispatching navigation state.
        const courseKey = numberToCourseKey[courseNumber];

        if (!courseKey) {
            return;
        }

        const membership = getPathwayMembership(courseNumber);
        const activePathway = membership[0] || '2';
        const activePathwayOrder = PATHWAYS[activePathway]
            .filter((num) => Boolean(numberToCourseKey[num]))
            .join(', ');

        dispatch(changeActiveCourse(courseKey));
        setStatusMessage(`Pathway ${activePathway} selected. Current step is course ${courseNumber}. Pathway order: ${activePathwayOrder}.`);
    }

    // Update angle element positioning between courses 246 and 405
    // by measuring the rendered circle centers and computing a line segment.
    const updateAngle = () => {
        const angleElement = document.querySelector('.curriculum-pathway .angle');
        const circle246 = document.querySelector('#course-btn-246 .circle');
        const circle405 = document.querySelector('#course-btn-405 .circle');

        if (!angleElement || !circle246 || !circle405) {
            return;
        }

        // Get bounding boxes of the circles
        const rect246 = circle246.getBoundingClientRect();
        const rect405 = circle405.getBoundingClientRect();

        // Calculate the centers of the circles
        const center246 = {
            x: rect246.left + rect246.width / 2,
            y: rect246.top + rect246.height / 2,
        };
        const center405 = {
            x: rect405.left + rect405.width / 2,
            y: rect405.top + rect405.height / 2,
        };

        // Calculate vector from 405 to 246.
        const dx = center246.x - center405.x;
        const dy = center246.y - center405.y;

        // Convert vector to distance + rotation so CSS can draw/connect the line.
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI); // Convert radians to degrees

        // Apply the calculated width and rotation to the .angle element
        angleElement.style.width = `${distance}px`;
        angleElement.style.transform = `rotate(${angle}deg)`;
    };

    // Apply loaded state class and keep angle aligned on viewport changes.
    useEffect(() => {
        const section = document.querySelector('.course-pathway-hero');

        if (section) {
            section.classList.add('loaded');
            section.classList.remove('loading');
        }

        // Update angle on initial load
        updateAngle();

        // Update angle on window resize
        window.addEventListener('resize', updateAngle);

        return () => {
            window.removeEventListener('resize', updateAngle);
        };
    }, []);

    /**
     * Derive tooltip text for a course button from the live availability data.
     *
     * Returns an empty string if the course has available instances.
     */
    function getAvailabilityTooltip(courseNumber) {
        const courseKey = numberToCourseKey[courseNumber];
        if (!courseKey) {
            return '';
        }
        const methodRecordId = courseDetailsProp[courseKey]?.methodRecordId;
        if (!methodRecordId) {
            return '';
        }
        const availability = pathwayAvailability[methodRecordId];
        if (!availability) {
            return '';
        }
        return availability.hasAvailability ? '' : availability.noAvailabilityMessage;
    }

    /**
     * Render a single pathway course node.
     * Handles:
     * - availability/disabled behavior
     * - accessibility labels and tooltip associations
     * - active styling and optional diagonal connector
     */
    function renderCourseButton(courseNumber, coursePathway, courseType, hasAngle = false) {
        const courseKey = numberToCourseKey[courseNumber];
        const details = courseKey ? courseDetailsProp[courseKey] : null;
        const isAvailable = Boolean(courseKey && details);
        const tooltipText = availabilityLoading ? '' : getAvailabilityTooltip(courseNumber);
        const hasTooltip = !isEmpty(tooltipText);
        const membership = getPathwayMembership(courseNumber);
        const isActive = activeCourseNumber === courseNumber;
        const resolvedCourseType = isAvailable ? getCourseTypeLabel(details) : courseType;
        const courseTitle = isAvailable ? details.title : `Course ${courseNumber}`;
        const PathIcon = resolvedCourseType === 'online' ? IconVideoPurple : IconPodiumPurple;
        const typeDisplayText = resolvedCourseType === 'online' ? 'Online Course' : 'In-Person Course';

        // Button + optional tooltip are wrapped so hover/focus styles can target both.
        return (
            <div className="course-button-wrapper" key={courseNumber}>
                <button
                    key={courseKey || courseNumber}
                    id={`course-btn-${courseNumber}`}
                    className={`course ${isActive ? 'is-active' : ''}`}
                    data-course={courseNumber}
                    data-course-pathway={coursePathway}
                    data-course-type={resolvedCourseType}
                    aria-current={isActive ? 'step' : undefined}
                    aria-pressed={isActive}
                    aria-label={`Course ${courseNumber}: ${courseTitle}. ${resolvedCourseType} course, ${getPathwayLabel(membership)}.${isAvailable ? '' : ' Course details are not currently available.'}`}
                    aria-describedby={`curriculum-pathway-description${hasTooltip ? ` course-tooltip-${courseNumber}` : ''}`}
                    disabled={!isAvailable}
                    onClick={isAvailable ? () => handleCourseClick(courseNumber) : undefined}
                    // Escape allows keyboard users to dismiss tooltip content-on-focus.
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') setDismissedTooltip(courseNumber);
                    }}
                    // Clear dismissal state when focus leaves, so tooltip can show again next focus.
                    onBlur={() => setDismissedTooltip(null)}
                    type="button"
                >
                    <span className="text">
                        {courseNumber}
                        <span aria-hidden="true"><PathIcon/></span>
                        <span className="sr-only">({typeDisplayText}) {courseTitle}</span>
                    </span>
                    {hasAngle && <span className="angle" aria-hidden="true"></span>}
                    <span className={`circle ${isActive ? 'circle--on' : ''}`} aria-hidden="true"></span>
                </button>
                {hasTooltip && (
                    <div
                        id={`course-tooltip-${courseNumber}`}
                        role="tooltip"
                        className="course-tooltip-box"
                        // Inline override only when dismissed via Escape.
                        style={dismissedTooltip === courseNumber ? {display: 'none'} : undefined}
                    >
                        {tooltipText}
                    </div>
                )}
            </div>
        );
    }

    // Component level JSX return statement ******************************************************
    return (
        <>
            <section className="course-pathway-hero loading">

                {/*  HEADER AREA  */}
                <div className="course-details-header">
                    <div className="container">
                        <h1 className="title">{`${(courseDetails.courseNumber) ? courseDetails.courseNumber + ': ' : ''} ${courseDetails.title}`}</h1>
                        {courseDetails.courseSubtitle && <h2 className="subtitle">{courseDetails.courseSubtitle}</h2>}
                        <h2 className="title-secondary">
                            <div
                                className="headline-label">Instructor{courseDetails.instructors.length > 1 ? 's' : ''}</div>
                            {courseDetails.instructors.map((instructor, i) => {
                                // if more than one instructor
                                if (i !== 0) {
                                    return (' & ' + instructor.Name)
                                } else {
                                    return (instructor.Name)
                                }
                            })}
                        </h2>
                    </div>
                </div>

                <div className="curriculum-pathway">
                    <div className="container container-small">
                        <div id="curriculum-pathway-description" className="sr-only">
                            This curriculum includes two valid pathways.
                            Pathway 1: courses 165, 246, 405, 505, and 603.
                            Pathway 2: courses 105, 205, 305, 405, 505, and 603.
                        </div>
                        <div className="sr-only" aria-live="polite" aria-atomic="true">{statusMessage}</div>

                        <div className="pathway-graphic" aria-describedby="curriculum-pathway-description">

                            <div className="curriculum-pathway-grid">
                                <div></div>

                                {renderCourseButton('165', '1', 'online')}

                                <div className="arrow" aria-hidden="true"><CaretGoldRight/></div>

                                {renderCourseButton('246', '1', 'online', true)}
                            </div>

                            <div className="curriculum-pathway-grid curriculum-pathway-grid--2">
                                {renderCourseButton('105', '2', 'in-person')}

                                <div className="arrow" aria-hidden="true"><CaretGoldRight/></div>

                                {renderCourseButton('205', '2', 'in-person')}

                                <div className="arrow" aria-hidden="true"><CaretGoldRight/></div>

                                {renderCourseButton('305', '2', 'in-person')}

                                <div className="arrow" aria-hidden="true"><CaretGoldRight/></div>

                                {renderCourseButton('405', '1 2', 'in-person')}

                                <div className="arrow" aria-hidden="true"><CaretGoldRight/></div>

                                {renderCourseButton('505', '1 2', 'in-person', false)}

                                <div className="arrow" aria-hidden="true"><CaretGoldRight/></div>

                                {renderCourseButton('603', '1 2', 'in-person')}
                            </div>
                            <div className="curriculum-pathway-key" aria-hidden="true">
                                <div className="curriculum-pathway-key__item"><IconVideoPurple/> Online Courses</div>
                                <div className="curriculum-pathway-key__item"><IconPodiumPurple/> In-Person Courses
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* COURSE META INFO */}
                <CourseMetaInfo courseDetails={courseDetails}/>

                {/* FOOTER AREA */}
                <CourseHeroFooter courseDetails={courseDetails}/>
            </section>
        </>
    );
}

export default CoursePathwayHero;
